/* ===========================================================================
   useTranslate — automatic site translation via the Google Translate widget.

   Mechanism (ported from the proven Haven & Co implementation):
   - A hidden #google_translate_element loads Google's translate widget.
   - Picking a language sets the `googtrans=/en/<code>` cookie, persists the
     choice to localStorage, then reloads. Google reads the cookie on load and
     translates the whole page (and keeps translating SPA route changes via its
     own observer).
   - Choosing English clears the cookie and reloads to restore the original.
   - Google's own banner / gadget UI is hidden (see assets/styles/translate.css
     plus the runtime observer below) so only our gold switcher is visible.

   The state is module-scoped so the switcher (SiteNav) and the app shell
   (App.vue) share one source of truth.
   =========================================================================== */
import { ref, nextTick } from 'vue';

/* Full language list (matches the Haven & Co guide). `code` is the Google
   Translate language code; `native` is shown in the switcher menu. */
export const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'sw', name: 'Swahili', native: 'Kiswahili' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'it', name: 'Italian', native: 'Italiano' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'ko', name: 'Korean', native: '한국어' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands' },
];

const STORAGE_KEY = 'gmt_selected_language';
const INCLUDED = LANGUAGES.map((l) => l.code).join(',');

/* Reactive current language code (lowercase, e.g. 'en' | 'sw' | 'fr'). */
const lang = ref('en');
/* Drives the branded full-screen loader (BrandLoader.vue). Starts true so the
   pulsing logo covers the initial load and, after a language switch, the reload
   + Google's translation pass. */
const loading = ref(true);
let initialized = false;

/* ---- cookie helpers ----------------------------------------------------- */
function readCookieLang() {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/en\/([a-z-]+)/i);
  return match ? match[1].toLowerCase() : 'en';
}
function writeCookieLang(code) {
  // Set on the current path and (where applicable) the bare domain so Google
  // picks it up on the next load regardless of host.
  document.cookie = `googtrans=/en/${code}; path=/; max-age=31536000`;
  const host = window.location.hostname;
  if (host && host.indexOf('.') !== -1) {
    document.cookie = `googtrans=/en/${code}; path=/; domain=.${host}; max-age=31536000`;
  }
}
function clearCookieLang() {
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `googtrans=; path=/; ${expire}`;
  const host = window.location.hostname;
  if (host && host.indexOf('.') !== -1) {
    document.cookie = `googtrans=; path=/; domain=.${host}; ${expire}`;
  }
}

/* ---- Google widget bootstrap ------------------------------------------- */
function ensureWidgetEl() {
  if (!document.getElementById('google_translate_element')) {
    const el = document.createElement('div');
    el.id = 'google_translate_element';
    el.style.display = 'none';
    document.body.appendChild(el);
  }
}

function loadGoogleScript() {
  if (window.googleTranslateElementInit) return;
  window.googleTranslateElementInit = () => {
    /* eslint-disable no-new */
    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        includedLanguages: INCLUDED,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      },
      'google_translate_element',
    );
  };
  const script = document.createElement('script');
  script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  document.head.appendChild(script);
}

/* Aggressively neutralise Google's injected chrome (banner, gadget) and the
   `body { top: 40px }` shift it applies. CSS handles the static cases; this
   observer catches anything injected later. */
function hideGoogleChrome() {
  const fix = () => {
    document.body.style.top = '0px';
    const banner = document.querySelector('.goog-te-banner-frame, .skiptranslate iframe');
    if (banner && banner.parentNode) banner.style.display = 'none';
  };
  fix();
  const observer = new MutationObserver(fix);
  observer.observe(document.body, { childList: true, subtree: true });
}

/* Keep the branded loader on screen until the page is ready: for English, a
   short minimum splash; for other languages, until Google has applied the
   translation (it adds `translated-ltr`/`translated-rtl` to <html>), so we
   never flash the un-translated English first. */
function manageLoader() {
  const start = Date.now();
  const MIN_MS = 800; // minimum time the splash stays visible
  const MAX_MS = 6000; // safety net if translation never reports back
  const hide = () => {
    const wait = Math.max(0, MIN_MS - (Date.now() - start));
    window.setTimeout(() => { loading.value = false; }, wait);
  };

  if (lang.value === 'en') {
    hide();
    return;
  }

  const html = document.documentElement;
  const translated = () => /\btranslated-(ltr|rtl)\b/.test(html.className);
  if (translated()) { hide(); return; }

  const observer = new MutationObserver(() => {
    if (translated()) { observer.disconnect(); hide(); }
  });
  observer.observe(html, { attributes: true, attributeFilter: ['class'] });
  window.setTimeout(() => { observer.disconnect(); loading.value = false; }, MAX_MS);
}

/* ---- public API --------------------------------------------------------- */
export function initTranslate() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Build-time prerender (vue.config.js): render clean base-English content with
  // no splash and no Google widget, so static snapshots are deterministic. Real
  // browsers never set this flag.
  if (window.__PRERENDER_INJECTED) {
    loading.value = false;
    return;
  }

  ensureWidgetEl();
  loadGoogleScript();
  hideGoogleChrome();

  // Sync reactive state from the saved preference / existing cookie.
  const saved = localStorage.getItem(STORAGE_KEY) || readCookieLang();
  lang.value = saved && LANGUAGES.some((l) => l.code === saved) ? saved : 'en';
  document.documentElement.lang = lang.value;

  manageLoader();
}

export function setLang(code) {
  const next = String(code).toLowerCase();
  if (next === lang.value) return;
  lang.value = next;

  if (next === 'en') {
    localStorage.removeItem(STORAGE_KEY);
    clearCookieLang();
  } else {
    localStorage.setItem(STORAGE_KEY, next);
    writeCookieLang(next);
  }

  // Show the branded loader, let it paint, then reload so Google applies
  // (or removes) the translation across the whole app.
  loading.value = true;
  nextTick(() => { window.setTimeout(() => window.location.reload(), 60); });
}

export function useTranslate() {
  return { lang, loading, LANGUAGES, setLang, initTranslate };
}
